// Reorders the points of a decoded point cloud block so that any prefix of the block is an even sample
// of it. The viewer then thins a block by drawing only its first points (`drawCount`) instead of choosing
// between all of them and none. Decoders run this once per block, before the GPU buffer is built;
// positions, colors and collision flags move together.
//
// A random shuffle gives even prefixes too, but scatters consecutive draws over the whole block, which
// costs GPU time on every frame, also for blocks drawn whole. Blocks arrive in a spatially coherent order
// (consecutive points are neighbours), so the reorder keeps most of it: points are grouped in runs of
// `GROUP` consecutive points, round r takes the r-th of each group (in a random rank per group) and
// sweeps the groups in their original order. Round 0, one point per group, is the same problem one level
// up and is ordered recursively, which keeps prefixes shorter than one round even as well. Each round
// sweeps the block's screen area once; on Mechanical-room, moving frames cost about 3% more GPU time per
// point with groups of 4 than in the original order, and about 19% with groups of 16.

/** An indexable numeric array: a typed array or a plain array of numbers. */
export interface HsMutableNumericArray {
  readonly length: number;
  [index: number]: number;
}

const GROUP: number = 4;

/**
 * Reorders `points` (xyz triples) with the per-point `colors` and `collisions` in place. The order only
 * depends on the point count, so repeated decodes and benchmark runs draw the same points. Per-point
 * arrays shorter than the point count (absent attributes) are left as they are.
 */
export function hsInterleavePointCloudOrder(
  points: HsMutableNumericArray,
  colors: HsMutableNumericArray,
  collisions: HsMutableNumericArray
): void {
  const count: number = Math.floor(points.length / 3);
  if (count < 2) {
    return;
  }
  const order: Uint32Array = hsPointCloudInterleaveOrder(count);
  const sourcePoints: Float64Array = copy(points, count * 3);
  for (let target: number = 0; target < count; target++) {
    const source: number = order[target] * 3;
    points[target * 3] = sourcePoints[source];
    points[target * 3 + 1] = sourcePoints[source + 1];
    points[target * 3 + 2] = sourcePoints[source + 2];
  }
  for (const values of [colors, collisions]) {
    if (values.length < count) {
      continue;
    }
    const source: Float64Array = copy(values, count);
    for (let target: number = 0; target < count; target++) {
      values[target] = source[order[target]];
    }
  }
}

/** The draw order of `count` points: entry t is the original index of the point drawn t-th. */
export function hsPointCloudInterleaveOrder(count: number): Uint32Array {
  const indices: Uint32Array = new Uint32Array(count);
  for (let i: number = 0; i < count; i++) {
    indices[i] = i;
  }
  const order: Uint32Array = new Uint32Array(count);
  const random: () => number = mulberry32((count ^ 0x9e3779b9) >>> 0);
  interleave(indices, order, 0, random);
  return order;
}

/** Writes `indices` (spatially ordered) to `out` from `offset` in interleaved order. */
function interleave(indices: Uint32Array, out: Uint32Array, offset: number, random: () => number): void {
  const length: number = indices.length;
  if (length <= GROUP) {
    const ranked: Uint32Array = indices.slice();
    shuffle(ranked, 0, length, random);
    out.set(ranked, offset);
    return;
  }
  const groups: number = Math.ceil(length / GROUP);
  // ranked[k * GROUP + r] is the point of group k drawn in round r.
  const ranked: Uint32Array = indices.slice();
  for (let group: number = 0; group < groups; group++) {
    shuffle(ranked, group * GROUP, Math.min(length, (group + 1) * GROUP), random);
  }
  const firsts: Uint32Array = new Uint32Array(groups);
  for (let group: number = 0; group < groups; group++) {
    firsts[group] = ranked[group * GROUP];
  }
  interleave(firsts, out, offset, random);
  let position: number = offset + groups;
  for (let round: number = 1; round < GROUP; round++) {
    for (let group: number = 0; group < groups; group++) {
      const index: number = group * GROUP + round;
      if (index < length) {
        out[position++] = ranked[index];
      }
    }
  }
}

function copy(values: HsMutableNumericArray, length: number): Float64Array {
  const result: Float64Array = new Float64Array(length);
  for (let i: number = 0; i < length; i++) {
    result[i] = values[i];
  }
  return result;
}

function shuffle(values: Uint32Array, start: number, end: number, random: () => number): void {
  for (let i: number = end - 1; i > start; i--) {
    const j: number = start + Math.floor(random() * (i - start + 1));
    const swap: number = values[i];
    values[i] = values[j];
    values[j] = swap;
  }
}

/** mulberry32: fast, seedable, and good enough to rank points. */
function mulberry32(seed: number): () => number {
  let state: number = seed;
  return (): number => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t: number = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
